import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * Custom preloading strategy that:
 * 1. Preloads routes marked with data.preload = true
 * 2. Delays preloading by specified milliseconds (data.preloadDelay)
 * 3. Respects slow connections (Network Information API)
 */
@Injectable({
    providedIn: 'root'
})
export class CustomPreloadingStrategy implements PreloadingStrategy {
    preload(route: Route, load: () => Observable<any>): Observable<any> {
        // Check if preloading is explicitly disabled or on slow connection
        if (this.shouldNotPreload(route)) {
            return of(null);
        }

        // Get preload delay from route data (default: 0ms)
        const delay = route.data?.['preloadDelay'] || 0;

        // Preload after delay
        if (delay > 0) {
            return timer(delay).pipe(
                mergeMap(() => {
                    console.log(`Preloading: ${route.path}`);
                    return load();
                })
            );
        }

        // Immediate preload
        console.log(`Preloading immediately: ${route.path}`);
        return load();
    }

    private shouldNotPreload(route: Route): boolean {
        // Don't preload if explicitly set to false
        if (route.data?.['preload'] === false) {
             
            return true;
        }

        // Check Network Information API for slow connections
        const connection = (navigator as any)?.connection;
        if (connection) {
            // Don't preload on 2G or slow-2g
            if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
                return true;
            }
            // Don't preload if save-data is enabled
            if (connection.saveData) {
                return true;
            }
        }

        // Default: allow preloading if not explicitly disabled
        return route.data?.['preload'] !== true;
    }
}
